/**
 * **A reaction shot a rule grants** (Covering Fire, Impending Threat, Moving Target): a reaction
 * `request_attack` that is not Overwatch. A reaction attack carries no reaction name, so the shot is
 * recognized from state: the target carries a grant status the attacker planted, and the weapon
 * that would fire is one the grant allows. Any other reaction attack is Overwatch. Three sites read
 * this one answer so they cannot disagree:
 * - `refereeIntent`'s reaction gates (the weapon's Range instead of its Threat, no Overwatch ban
 *   on Ordnance);
 * - `intentCost` (the shot spends the grant's own named reaction, not `overwatch`);
 * - the room's `stampReactionDamageScale` (Covering Fire's shot is halved; nothing else is).
 *
 * A new grant is a row in {@link REACTION_SHOT_GRANTS}. The browser's chooser reads the same rows
 * ({@link reactionShotGrantFor}), so a weapon the server would accept is the one it offers.
 */
import type { EncounterState, WeaponProfile } from "@lancer-vtt/domain";
import type { EncounterIntent } from "@lancer-vtt/shared";
import { namesUnknownWeapon, selectWeapon } from "../resolve";
import { COVERING_FIRE_MARK_STATUS, COVERING_FIRE_REACTION, isHeavyRangedWeapon } from "./coveringFire";

/** What a granted shot changes from Overwatch. */
export type ReactionShotGrant = {
  /** The named reaction cap the shot spends. */
  reactionId: string;
  /** The rule's name in a refusal ("covering fire: …"). */
  verb: string;
  /** The reaction's printed name, for the COMMS card and the feed ("Impending Threat"). */
  name: string;
  /** Covering Fire's "for half damage". */
  damageScale?: "half";
};

/** One grant: the status on the target that carries it (sourced from the shooter), and the weapons it allows. */
export type ReactionShotGrantSpec = ReactionShotGrant & {
  status: string;
  weapon(w: WeaponProfile): boolean;
  /**
   * An interrupt (`moveInterrupts.ts`) whose mover has no choice: the shot is asked straight
   * after the watcher's Use (Suppress's Moving Target: "interrupt with a single weapon's attack").
   */
  moverChooses?: false;
  /** Statuses the shooter planted on the mover that a taken shot ends (Suppress: "lasts until the Archer uses Moving Target"). */
  ends?: readonly string[];
};

/**
 * The markers an interrupting reaction plants on the mover once it chose to keep moving
 * ("continue but let you immediately attack them", `moveInterrupts.ts`). `this_turn`; the move's
 * referee clears it once the shot is answered.
 */
export const IMPENDING_THREAT_SHOT_STATUS = "Impending Threat Shot";
export const MOVING_TARGET_SHOT_STATUS = "Moving Target Shot";
export const SUPPRESS_SHOT_STATUS = "Suppress Shot";
/** The mark Suppress plants on its target (`entries/npcs/suppress-archer.ts`). */
export const SUPPRESSED_STATUS = "Suppressed";
/** The Sniper's Anti-materiel Rifle, the one weapon Moving Target fires. */
export const ANTI_MATERIEL_RIFLE_ID = "npcf_anti_materiel_rifle_sniper";

/**
 * Every reaction shot a rule grants, keyed on the status the shooter planted on the target.
 *   - Covering Fire (`t_heavy_gunner:1`, Core p.97): a Heavy ranged weapon, for half damage.
 *   - Impending Threat (Archer): "attack them with a single weapon", any weapon.
 *   - Moving Target (Sniper): "attacked by you using the Anti-materiel Rifle".
 *   - Suppress (Archer), its Moving Target: "interrupt with a single weapon's attack", no choice for
 *     the mover; using it ends Suppress (the mark and the Impaired it gave).
 */
export const REACTION_SHOT_GRANTS: readonly ReactionShotGrantSpec[] = [
  { status: COVERING_FIRE_MARK_STATUS, reactionId: COVERING_FIRE_REACTION, verb: "covering fire", name: "Covering Fire", damageScale: "half", weapon: isHeavyRangedWeapon },
  { status: IMPENDING_THREAT_SHOT_STATUS, reactionId: "npcf_impending_threat_archer", verb: "impending threat", name: "Impending Threat", weapon: () => true },
  { status: MOVING_TARGET_SHOT_STATUS, reactionId: "npcf_moving_target_sniper", verb: "moving target", name: "Moving Target", weapon: (w) => w.id === ANTI_MATERIEL_RIFLE_ID },
  { status: SUPPRESS_SHOT_STATUS, reactionId: "npcf_suppress_archer", verb: "suppress", name: "Moving Target", weapon: () => true, moverChooses: false, ends: [SUPPRESSED_STATUS, "Impaired"] },
];

/**
 * The grant a reaction shot by `attacker` at `target` with `weapon` rides, or `undefined` for an
 * Overwatch: the first {@link REACTION_SHOT_GRANTS} row whose status the attacker planted on the
 * target and whose weapon rule admits `weapon`.
 */
export function reactionShotGrantFor(args: { state: EncounterState; attacker: string; target: string; weapon: WeaponProfile }): ReactionShotGrant | undefined {
  const { state, attacker, target, weapon } = args;
  const statuses = state.combatants[target]?.statuses ?? [];
  for (const spec of REACTION_SHOT_GRANTS) {
    if (!statuses.some((s) => s.name === spec.status && s.source === attacker)) continue;
    if (!spec.weapon(weapon)) continue;
    return { reactionId: spec.reactionId, verb: spec.verb, name: spec.name, ...(spec.damageScale !== undefined ? { damageScale: spec.damageScale } : {}) };
  }
  return undefined;
}

/** {@link reactionShotGrantFor} over an intent: a non-tech reaction `request_attack` at a unit, with a weapon the attacker carries. */
export function reactionShotGrant(state: EncounterState, intent: EncounterIntent): ReactionShotGrant | undefined {
  if (intent.type !== "request_attack" || intent.reaction !== true || intent.tech === true) return undefined;
  if (intent.target.kind !== "unit") return undefined;
  const atk = state.combatants[intent.attacker];
  if (!atk || namesUnknownWeapon(atk, intent.weapon)) return undefined;
  return reactionShotGrantFor({ state, attacker: intent.attacker, target: intent.target.id, weapon: selectWeapon(atk, intent.weapon, intent.profile) });
}

/** The named reaction every reaction attack no rule grants spends. */
export const OVERWATCH_REACTION = "overwatch";

/**
 * The named reaction a reaction attack spends: its grant's ({@link reactionShotGrant}), else
 * Overwatch; `undefined` for an attack that isn't a reaction. The cost charges it and the
 * attack-family `reaction` event fact carries it, so a clause gated on a granted shot (Heavy
 * Gunner 2 on the Covering Fire shot) and the slot it spent cannot disagree.
 */
export function reactionAttackName(state: EncounterState, intent: EncounterIntent): string | undefined {
  if (intent.type !== "request_attack" || intent.reaction !== true) return undefined;
  return reactionShotGrant(state, intent)?.reactionId ?? OVERWATCH_REACTION;
}
